"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import {
  Users,
  Search,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Mail,
  Phone,
  Copy,
  Check,
  MessageSquare,
  Download,
  Info
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DualScrollWrapper } from "@/components/directory/DualScrollWrapper";
import { PremiumMultiSelect } from "@/components/directory/PremiumMultiSelect";
import { SortToggle } from "@/components/directory/SortToggle";
import { PhaseHelpTooltip } from "@/components/meetings/PhaseHelpTooltip";
import { GENDER_AI_HELP } from "@/components/directory/genderAiHelp";
import { fetchWithAuth } from "@/lib/auth";

const API_BASE = (typeof window !== "undefined") ? "" : (process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001");

const ROLE_MAPPING: Record<string, string> = {
  'charterholder': 'Charterholder',
  'level3candidate': 'L3 Candidate',
  'level2candidate': 'L2 Candidate',
  'level1candidate': 'L1 Candidate',
  'professional_member': 'Professional Member',
  'affiliate_member': 'Affiliate Member',
  'charterholderinactive': 'Charterholder (Inact.)',
  'l3eligible': 'L3 Eligible',
  'l2eligible': 'L2 Eligible',
  'l1eligible': 'L1 Eligible',
  'professional_member_inactive': 'Professional (Inact.)',
  'affiliate_member_inactive': 'Affiliate (Inact.)',
  'Never a Candidate': 'Never a Candidate',
  'Never a Member': 'Never a Member',
  'l3_cleared': 'L3 Cleared'
};

interface StaffRow {
  member_id: string;
  first_name: string;
  last_name: string;
  city_name: string;
  city_country: string;
  city_admin1: string;
  program_journey: string;
  membership_journey: string;
  cluster_center_name: string;
  nearest_cluster_center_name: string;
  chapter: string;
  region: string;
  is_active: boolean;
  id_discord: string;
  email_1: string;
  phone_1: string;
  administration: string;
  /**
   * Inferred by an AI model from the member's name — never self-reported, and
   * only kept when the model was >90% confident (everything else arrives as
   * "Unknown"). Always render it labelled as a prediction; see
   * backend/pipeline/predict_gender.py. A member's own answer lives on
   * users.gender_self and takes precedence wherever both are shown.
   */
  gender_ai?: string | null;
}

export function StaffDirectoryPanel() {
  const [data, setData] = useState<StaffRow[]>([]);
  const [fetching, setFetching] = useState(false);
  const fetchAbortRef = useRef<AbortController | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [sortConfig, setSortConfig] = useState<{ col: string | null, order: 'asc' | 'desc' }>({ col: null, order: 'asc' });

  const [filterOptions, setFilterOptions] = useState<{
    regions: string[];
    chapters: string[];
    clusters: string[];
    countries: string[];
    localities: string[];
    states: string[];
    nearest_clusters: string[];
    program_journeys: string[];
    membership_journeys: string[];
    genders: string[];
  }>({ regions: [], chapters: [], clusters: [], countries: [], localities: [], states: [], nearest_clusters: [], program_journeys: [], membership_journeys: [], genders: [] });

  const [filterRegion, setFilterRegion] = useState<string[]>([]);
  const [excludeRegion, setExcludeRegion] = useState(false);
  const [filterChapter, setFilterChapter] = useState<string[]>([]);
  const [excludeChapter, setExcludeChapter] = useState(false);
  const [filterCluster, setFilterCluster] = useState<string[]>([]);
  const [excludeCluster, setExcludeCluster] = useState(false);
  const [filterCountry, setFilterCountry] = useState<string[]>([]);
  const [excludeCountry, setExcludeCountry] = useState(false);
  const [filterLocality, setFilterLocality] = useState<string[]>([]);
  const [excludeLocality, setExcludeLocality] = useState(false);
  const [filterState, setFilterState] = useState<string[]>([]);
  const [excludeState, setExcludeState] = useState(false);
  const [filterNearestCluster, setFilterNearestCluster] = useState<string[]>([]);
  const [excludeNearestCluster, setExcludeNearestCluster] = useState(false);
  const [filterGender, setFilterGender] = useState<string[]>([]);
  const [excludeGender, setExcludeGender] = useState(false);
  const [filterNameSearch, setFilterNameSearch] = useState("");
  const [debouncedNameSearch, setDebouncedNameSearch] = useState("");
  const [filterIdSearch, setFilterIdSearch] = useState("");
  const [debouncedIdSearch, setDebouncedIdSearch] = useState("");
  const [filterDiscordSearch, setFilterDiscordSearch] = useState("");
  const [debouncedDiscordSearch, setDebouncedDiscordSearch] = useState("");
  const [filterEmailSearch, setFilterEmailSearch] = useState("");
  const [debouncedEmailSearch, setDebouncedEmailSearch] = useState("");
  const [filterPhoneSearch, setFilterPhoneSearch] = useState("");
  const [debouncedPhoneSearch, setDebouncedPhoneSearch] = useState("");
  const [filterPhoneBlank, setFilterPhoneBlank] = useState<string[]>([]);
  const [excludePhoneBlank, setExcludePhoneBlank] = useState(false);
  const [filterDiscordBlank, setFilterDiscordBlank] = useState<string[]>([]);
  const [excludeDiscordBlank, setExcludeDiscordBlank] = useState(false);
  const [filterProgramJourney, setFilterProgramJourney] = useState<string[]>([]);
  const [excludeProgramJourney, setExcludeProgramJourney] = useState(false);
  const [filterMembershipJourney, setFilterMembershipJourney] = useState<string[]>([]);
  const [excludeMembershipJourney, setExcludeMembershipJourney] = useState(false);
  const [filterActive, setFilterActive] = useState<boolean | null>(null);

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const buildParams = useCallback((extra?: Record<string, string>) => {
    const params = new URLSearchParams(extra);
    if (debouncedSearch) params.append("search", debouncedSearch);
    filterRegion.forEach(r => params.append("region", r));
    if (excludeRegion && filterRegion.length > 0) params.append("region_exclude", "true");
    filterChapter.forEach(c => params.append("chapter", c));
    if (excludeChapter && filterChapter.length > 0) params.append("chapter_exclude", "true");
    filterCluster.forEach(c => params.append("cluster", c));
    if (excludeCluster && filterCluster.length > 0) params.append("cluster_exclude", "true");
    filterCountry.forEach(c => params.append("country", c));
    if (excludeCountry && filterCountry.length > 0) params.append("country_exclude", "true");
    filterLocality.forEach(l => params.append("locality", l));
    if (excludeLocality && filterLocality.length > 0) params.append("locality_exclude", "true");
    filterNearestCluster.forEach(n => params.append("nearest_cluster", n));
    if (excludeNearestCluster && filterNearestCluster.length > 0) params.append("nearest_cluster_exclude", "true");
    filterState.forEach(s => params.append("state", s));
    if (excludeState && filterState.length > 0) params.append("state_exclude", "true");
    if (debouncedNameSearch) params.append("name_search", debouncedNameSearch);
    if (debouncedIdSearch) params.append("id_search", debouncedIdSearch);
    if (debouncedDiscordSearch) params.append("discord_search", debouncedDiscordSearch);
    if (debouncedEmailSearch) params.append("email_search", debouncedEmailSearch);
    if (debouncedPhoneSearch) params.append("phone_search", debouncedPhoneSearch);
    filterPhoneBlank.forEach(p => params.append("phone_blank", p));
    if (excludePhoneBlank && filterPhoneBlank.length > 0) params.append("phone_blank_exclude", "true");
    filterDiscordBlank.forEach(d => params.append("discord_blank", d));
    if (excludeDiscordBlank && filterDiscordBlank.length > 0) params.append("discord_blank_exclude", "true");
    filterProgramJourney.forEach(p => params.append("program_journey", p));
    if (excludeProgramJourney && filterProgramJourney.length > 0) params.append("program_journey_exclude", "true");
    filterMembershipJourney.forEach(m => params.append("membership_journey", m));
    if (excludeMembershipJourney && filterMembershipJourney.length > 0) params.append("membership_journey_exclude", "true");
    filterGender.forEach(g => params.append("gender", g));
    if (excludeGender && filterGender.length > 0) params.append("gender_exclude", "true");
    if (filterActive !== null) params.append("is_active", filterActive.toString());
    return params;
  }, [
    debouncedSearch, filterRegion, excludeRegion, filterChapter, excludeChapter,
    filterCluster, excludeCluster, filterCountry, excludeCountry, filterLocality,
    excludeLocality, filterNearestCluster, excludeNearestCluster, filterState, excludeState,
    debouncedNameSearch, debouncedIdSearch, debouncedDiscordSearch, debouncedEmailSearch, debouncedPhoneSearch,
    filterPhoneBlank, excludePhoneBlank,
    filterDiscordBlank, excludeDiscordBlank,
    filterProgramJourney, excludeProgramJourney, filterMembershipJourney, excludeMembershipJourney,
    filterGender, excludeGender, filterActive
  ]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      if (sortConfig.col) {
        params.append("sort_col", sortConfig.col);
        params.append("sort_order", sortConfig.order);
      }
      const res = await fetchWithAuth(`${API_BASE}/api/v1/directory/staff-directory/export-csv?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      
      const dateStr = new Date().toISOString().slice(0, 10);
      a.download = `staff_directory_${dateStr}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed: " + (e instanceof Error ? e.message : e));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameSearch(filterNameSearch);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterNameSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedIdSearch(filterIdSearch);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterIdSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDiscordSearch(filterDiscordSearch);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterDiscordSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmailSearch(filterEmailSearch);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterEmailSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPhoneSearch(filterPhoneSearch);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterPhoneSearch]);

  useEffect(() => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setFetching(true);

    const dirParams = buildParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (sortConfig.col) {
      dirParams.append("sort_col", sortConfig.col);
      dirParams.append("sort_order", sortConfig.order);
    }

    const filterParams = buildParams();
    
    Promise.all([
      fetchWithAuth(`${API_BASE}/api/v1/directory/staff-directory?${dirParams.toString()}`, { signal: controller.signal }),
      fetchWithAuth(`${API_BASE}/api/v1/directory/staff-filters?${filterParams.toString()}`, { signal: controller.signal }),
    ])
      .then(async ([dirRes, filterRes]) => {
        if (!dirRes.ok || !filterRes.ok) {
          throw new Error(`Directory fetch failed: dir status=${dirRes.status}, filters status=${filterRes.status}`);
        }
        return Promise.all([dirRes.json(), filterRes.json()]);
      })
      .then(([dirData, filterData]) => {
        setData(dirData.data || []);
        setTotalPages(dirData.total_pages || 1);
        setTotalRecords(dirData.total || 0);
        if (filterData && typeof filterData === "object" && !("detail" in filterData)) {
          setFilterOptions(filterData);
        }
        setFetching(false);
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          console.error("Error fetching staff data:", err);
          setFetching(false);
        }
      });

    return () => { controller.abort(); };
  }, [page, pageSize, sortConfig, buildParams]);

  const hasAnyFilter = filterRegion.length > 0 || filterChapter.length > 0 || filterCluster.length > 0 || filterCountry.length > 0 || filterLocality.length > 0 || filterNearestCluster.length > 0 || filterState.length > 0 || searchTerm || filterNameSearch || filterIdSearch || filterDiscordSearch || filterDiscordBlank.length > 0 || filterEmailSearch || filterPhoneSearch || filterPhoneBlank.length > 0 || filterActive !== null || filterProgramJourney.length > 0 || filterMembershipJourney.length > 0 || filterGender.length > 0;

  return (
    <motion.section
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className="space-y-6"
    >
      <div className="flex flex-col gap-6 px-4">
        <div className="space-y-1">
          <h2 className="text-3xl font-normal text-[#004D57] uppercase tracking-tighter flex items-center gap-4">
            <Users className="w-8 h-8 text-[#AE232D]" />
            Staff Directory
          </h2>
          <p className="text-slate-500 text-[10px] font-normal uppercase tracking-widest">Internal Staff View: Direct access to member contact details, journeys, and community assignments.</p>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap items-center gap-3 w-full justify-start">
          <div className="relative group flex-1 w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-[#004D57] transition-colors" />
            <Input
              placeholder="GLOBAL KEYWORD SEARCH..."
              className="pl-12 h-14 bg-white border-slate-100 rounded-2xl shadow-xl focus:ring-2 focus:ring-[#004D57] font-normal text-xs tracking-widest uppercase"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 shrink-0">
            {[
              { label: 'ALL', value: null },
              { label: 'ACTIVE', value: true },
              { label: 'INACTIVE', value: false }
            ].map((opt) => (
              <button
                key={opt.label}
                onClick={() => { setFilterActive(opt.value); setPage(1); }}
                className={cn(
                  "px-4 py-2 rounded-lg text-[9px] font-bold tracking-widest transition-all",
                  filterActive === opt.value 
                    ? "bg-white text-[#004D57] shadow-sm" 
                    : "text-slate-400 hover:text-slate-600"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <Button
            onClick={exportCSV}
            disabled={exporting || fetching || totalRecords === 0}
            className="h-14 px-6 bg-[#004D57] hover:bg-[#004D57]/90 text-white font-normal text-[10px] uppercase tracking-widest rounded-2xl shrink-0 flex items-center gap-2 shadow-xl hover:shadow-2xl transition-all disabled:opacity-55"
          >
            {exporting ? (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>

          {hasAnyFilter && (
            <Button
              onClick={() => {
                setFilterRegion([]);
                setExcludeRegion(false);
                setFilterChapter([]);
                setExcludeChapter(false);
                setFilterCluster([]);
                setExcludeCluster(false);
                setFilterCountry([]);
                setExcludeCountry(false);
                setFilterLocality([]);
                setExcludeLocality(false);
                setFilterNearestCluster([]);
                setExcludeNearestCluster(false);
                setFilterState([]);
                setExcludeState(false);
                setFilterNameSearch("");
                setFilterIdSearch("");
                setFilterDiscordSearch("");
                setFilterDiscordBlank([]);
                setExcludeDiscordBlank(false);
                setFilterEmailSearch("");
                setFilterPhoneSearch("");
                setFilterPhoneBlank([]);
                setExcludePhoneBlank(false);
                setFilterProgramJourney([]);
                setExcludeProgramJourney(false);
                setFilterMembershipJourney([]);
                setExcludeMembershipJourney(false);
                setFilterGender([]);
                setExcludeGender(false);
                setFilterActive(null);
                setSearchTerm("");
                setPage(1);
              }}
              variant="ghost"
              className="h-10 px-4 border border-dashed border-[#AE232D]/30 text-[#AE232D] font-bold text-[9px] uppercase tracking-widest hover:bg-[#AE232D]/5 rounded-xl transition-all shrink-0"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-[3rem] border border-slate-50 shadow-2xl overflow-hidden p-2 relative min-h-[600px] flex flex-col">
         {fetching && (
           <div className="absolute top-2 left-2 right-2 h-0.5 z-30 overflow-hidden rounded-full">
             <div className="h-full bg-[#AE232D] rounded-full" style={{ animation: 'progress-slide 1.2s ease-in-out infinite' }} />
           </div>
         )}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-t-3xl">
          <span className="text-[10px] font-normal text-[#004D57] uppercase tracking-widest">
            {totalRecords.toLocaleString()} Total Records &nbsp;·&nbsp; Showing page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || fetching}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-white border border-slate-200 rounded-xl font-normal text-[9px] uppercase tracking-widest text-[#004D57] hover:bg-[#004D57] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3 h-3" /> Prev
            </button>
            <span className="text-[10px] font-normal text-slate-400 tabular-nums">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || fetching}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-[#004D57] text-white rounded-xl font-normal text-[9px] uppercase tracking-widest hover:bg-[#005D69] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
        <DualScrollWrapper>
          <div className="rounded-2xl border border-slate-50 relative min-w-max">
            <table className="w-full border-collapse text-left">
              <thead className="sticky top-0 z-20 shadow-sm">
                <tr className="bg-[#004D57] text-white">
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[50px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="member_id" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <div className="relative group/search">
                        <Input
                          type="text"
                          placeholder="ID..."
                          value={filterIdSearch}
                          onChange={(e) => setFilterIdSearch(e.target.value)}
                          className="h-6 bg-white/5 border-white/10 text-[9px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all pl-5 pr-2 rounded-md font-normal lowercase tracking-tight w-full max-w-[45px]"
                        />
                        <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40 group-focus-within/search:text-white/70 transition-colors" />
                      </div>
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[120px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="name" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <div className="relative group/search">
                        <Input
                          type="text"
                          placeholder="NAME..."
                          value={filterNameSearch}
                          onChange={(e) => setFilterNameSearch(e.target.value)}
                          className="h-6 bg-white/5 border-white/10 text-[9px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all pl-5 pr-2 rounded-md font-normal lowercase tracking-tight w-full max-w-[110px]"
                        />
                        <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40 group-focus-within/search:text-white/70 transition-colors" />
                      </div>
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="program_journey" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <PremiumMultiSelect
                        label="PROGRAM JOURNEY"
                        plural="PROGRAM STATUSES"
                        options={filterOptions.program_journeys}
                        selected={filterProgramJourney}
                        isExclude={excludeProgramJourney}
                        onExcludeToggle={setExcludeProgramJourney}
                        onChange={(vals) => { setFilterProgramJourney(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="membership_journey" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <PremiumMultiSelect
                        label="MEMBERSHIP JOURNEY"
                        plural="MEMBERSHIP STATUSES"
                        options={filterOptions.membership_journeys}
                        selected={filterMembershipJourney}
                        isExclude={excludeMembershipJourney}
                        onExcludeToggle={setExcludeMembershipJourney}
                        onChange={(vals) => { setFilterMembershipJourney(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[110px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="gender" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      {/* The caveat used to be a native title= attribute, which truncates and
                          is easy to miss. PhaseHelpTooltip portals out of DualScrollWrapper's
                          horizontal overflow, which would clip a positioned tooltip here. */}
                      <PhaseHelpTooltip
                        title={GENDER_AI_HELP.title}
                        body={GENDER_AI_HELP.body}
                        items={GENDER_AI_HELP.items}
                        align="left"
                      >
                        <span
                          className="inline-flex items-center gap-1 text-[8px] normal-case tracking-normal text-amber-600/80 cursor-help"
                          aria-label="How the gender value is produced"
                        >
                          AI estimate <Info size={10} aria-hidden="true" />
                        </span>
                      </PhaseHelpTooltip>
                      <PremiumMultiSelect
                        label="GENDER"
                        plural="GENDERS"
                        options={filterOptions.genders}
                        selected={filterGender}
                        isExclude={excludeGender}
                        onExcludeToggle={setExcludeGender}
                        onChange={(vals) => { setFilterGender(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="cluster" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <PremiumMultiSelect
                        label="COMMUNITY"
                        plural="COMMUNITIES"
                        options={filterOptions.clusters}
                        selected={filterCluster}
                        isExclude={excludeCluster}
                        onExcludeToggle={setExcludeCluster}
                        onChange={(vals) => { setFilterCluster(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="nearest_cluster" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <PremiumMultiSelect
                        label="NEAREST COMMUNITY"
                        plural="NEAREST COMMUNITIES"
                        options={filterOptions.nearest_clusters}
                        selected={filterNearestCluster}
                        isExclude={excludeNearestCluster}
                        onExcludeToggle={setExcludeNearestCluster}
                        onChange={(vals) => { setFilterNearestCluster(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="chapter" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <PremiumMultiSelect
                        label="CHAPTER"
                        plural="CHAPTERS"
                        options={filterOptions.chapters}
                        selected={filterChapter}
                        isExclude={excludeChapter}
                        onExcludeToggle={setExcludeChapter}
                        onChange={(vals) => { setFilterChapter(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="region" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <PremiumMultiSelect
                        label="REGION"
                        plural="REGIONS"
                        options={filterOptions.regions}
                        selected={filterRegion}
                        isExclude={excludeRegion}
                        onExcludeToggle={setExcludeRegion}
                        onChange={(vals) => { setFilterRegion(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="locality" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <PremiumMultiSelect
                        label="CITY"
                        plural="LOCALITIES"
                        options={filterOptions.localities}
                        selected={filterLocality}
                        isExclude={excludeLocality}
                        onExcludeToggle={setExcludeLocality}
                        onChange={(vals) => { setFilterLocality(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="state" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <PremiumMultiSelect
                        label="STATE"
                        plural="STATES"
                        options={filterOptions.states}
                        selected={filterState}
                        isExclude={excludeState}
                        onExcludeToggle={setExcludeState}
                        onChange={(vals) => { setFilterState(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                  <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                    <div className="flex flex-col gap-2">
                      <SortToggle col="country" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                      <PremiumMultiSelect
                        label="COUNTRY"
                        plural="COUNTRIES"
                        options={filterOptions.countries}
                        selected={filterCountry}
                        isExclude={excludeCountry}
                        onExcludeToggle={setExcludeCountry}
                        onChange={(vals) => { setFilterCountry(vals); setPage(1); }}
                      />
                    </div>
                  </th>
                   <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[140px]">
                     <div className="flex flex-col gap-2">
                        <SortToggle col="email" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                        <div className="relative group/search">
                          <Input
                            type="text"
                            placeholder="EMAIL..."
                            value={filterEmailSearch}
                            onChange={(e) => setFilterEmailSearch(e.target.value)}
                            className="h-6 bg-white/5 border-white/10 text-[9px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all pl-5 pr-2 rounded-md font-normal lowercase tracking-tight w-full max-w-[130px]"
                          />
                          <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40 group-focus-within/search:text-white/70 transition-colors" />
                        </div>
                     </div>
                   </th>
                   <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[120px]">
                      <div className="flex flex-col gap-2">
                         <SortToggle col="phone" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                         <PremiumMultiSelect
                           label="PHONE"
                           plural="PHONE OPTIONS"
                           options={["BLANKS"]}
                           selected={filterPhoneBlank}
                           isExclude={excludePhoneBlank}
                           onExcludeToggle={setExcludePhoneBlank}
                           onChange={(vals) => { setFilterPhoneBlank(vals); setPage(1); }}
                           searchValue={filterPhoneSearch}
                           onSearchChange={setFilterPhoneSearch}
                         />
                      </div>
                   </th>
                   <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] whitespace-nowrap min-w-[100px]">
                      <div className="flex flex-col gap-2">
                        <SortToggle col="id_discord" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                        <PremiumMultiSelect
                          label="DISCORD"
                          plural="DISCORD OPTIONS"
                          options={["BLANKS"]}
                          selected={filterDiscordBlank}
                          isExclude={excludeDiscordBlank}
                          onExcludeToggle={setExcludeDiscordBlank}
                          onChange={(vals) => { setFilterDiscordBlank(vals); setPage(1); }}
                          searchValue={filterDiscordSearch}
                          onSearchChange={setFilterDiscordSearch}
                        />
                      </div>
                    </th>
                </tr>
              </thead>
              <tbody className={cn("divide-y divide-slate-50 transition-opacity duration-200", fetching && "opacity-40 pointer-events-none")}>
                {data.length === 0 && !fetching ? (
                  <tr>
                    <td colSpan={15} className="p-20 text-center text-slate-400 font-normal uppercase text-xs tracking-widest">No matching staff directory records found.</td>
                  </tr>
                ) : data.length === 0 && fetching ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      {Array.from({ length: 15 }).map((_, j) => (
                        <td key={j} className="p-1 px-2">
                          <div className="h-3 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 50}%`, animationDelay: `${(i * 15 + j) * 20}ms` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  data.map((row, idx) => (
                    <tr
                      key={`${row.member_id}-${idx}`}
                      className="group hover:bg-[#F8F9FA] transition-colors border-b border-slate-50"
                    >
                      <td className="p-1 px-2 text-[10px] font-normal text-[#AE232D] font-mono whitespace-nowrap">{row.member_id}</td>
                      <td className="p-1 px-2">
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-1.5 h-1.5 rounded-full shrink-0",
                            row.is_active ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]" : "bg-slate-300"
                          )} title={row.is_active ? "Active" : "Inactive"} />
                          <span className="text-[11px] font-normal text-[#004D57] uppercase leading-tight">{row.first_name} {row.last_name}</span>
                        </div>
                      </td>
                      <td className="p-1 px-2">
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-[8px] font-normal px-2 py-1 bg-blue-50/50 text-blue-600/70 border border-blue-100/50 rounded uppercase tracking-widest leading-none">
                            {ROLE_MAPPING[row.program_journey] || row.program_journey || "—"}
                          </span>
                          {row.administration && row.program_journey.toLowerCase().includes("candidate") && !row.administration.toLowerCase().includes("june 2026") && !row.administration.toLowerCase().includes("jun 2026") && !row.administration.toLowerCase().includes("june 26") && (
                            <span className="text-[6.5px] font-medium px-1.5 py-0.5 bg-amber-50 text-amber-600/90 border border-amber-200/50 rounded uppercase tracking-widest leading-none" title="Actual Administration">
                              {row.administration}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-1 px-2">
                        <span className="text-[8px] font-normal px-2 py-1 bg-emerald-50/50 text-emerald-600/70 border border-emerald-100/50 rounded uppercase tracking-widest">{ROLE_MAPPING[row.membership_journey] || row.membership_journey || "—"}</span>
                      </td>
                      <td className="p-1 px-2">
                        {row.gender_ai && row.gender_ai !== "Unknown" ? (
                          <span
                            className="text-[8px] font-normal px-2 py-1 bg-amber-50/60 text-amber-700/80 border border-amber-200/50 rounded uppercase tracking-widest leading-none"
                            title="AI-predicted from the member's name — not self-reported"
                          >
                            {row.gender_ai}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-[10px] block text-center" title="No confident prediction">&mdash;</span>
                        )}
                      </td>
                      <td className="p-1 px-2">
                        <span className="text-[11px] font-normal opacity-80 uppercase tracking-tight">{row.cluster_center_name || "Unassigned"}</span>
                      </td>
                      <td className="p-1 px-2">
                        <span className="text-[11px] font-normal opacity-80 uppercase tracking-tight">{row.nearest_cluster_center_name || "Unassigned"}</span>
                      </td>
                      <td className="p-1 px-2">
                        <span className="text-[11px] font-normal opacity-80 uppercase tracking-tight">{row.chapter || "Unassigned"}</span>
                      </td>
                      <td className="p-1 px-2">
                        <span className="text-[11px] font-normal opacity-80 uppercase tracking-tight">{row.region || "Unassigned"}</span>
                      </td>
                      <td className="p-1 px-2">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-2.5 h-2.5 text-[#A9C4BB]" />
                          <span className="text-[11px] font-normal text-slate-600 uppercase leading-snug">
                            {row.city_name || "Unassigned"}
                          </span>
                        </div>
                      </td>
                      <td className="p-1 px-2">
                        <span className="text-[11px] font-normal text-slate-500 uppercase tracking-tight">{row.city_admin1 || "Unassigned"}</span>
                      </td>
                      <td className="p-1 px-2">
                        <span className="text-[11px] font-normal text-slate-400 uppercase tracking-tight">{row.city_country}</span>
                      </td>
                       <td className="p-1 px-2 border-r border-slate-50">
                         {row.email_1 ? (
                           <div className="flex items-center justify-between gap-2 p-1 px-2 bg-slate-50 hover:bg-[#AE232D]/5 rounded border border-slate-100 transition-all group/item">
                             <span className="text-[9px] font-mono lowercase truncate text-slate-600 group-hover/item:text-[#AE232D]">{row.email_1}</span>
                             <button
                               onClick={() => handleCopy(row.email_1, `${row.member_id}-email`)}
                               className="hover:scale-110 transition-transform p-0.5"
                               title="Copy email"
                             >
                               {copiedId === `${row.member_id}-email` ? (
                                 <Check className="w-2.5 h-2.5 text-emerald-500" />
                               ) : (
                                 <Copy className="w-2.5 h-2.5 text-slate-300 group-hover/item:text-[#AE232D]/40 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                               )}
                             </button>
                           </div>
                         ) : <span className="text-slate-300 text-[10px] block text-center">—</span>}
                       </td>
                       <td className="p-1 px-2">
                         {row.phone_1 ? (
                           <div className="flex items-center justify-between gap-2 p-1 px-2 bg-slate-50 hover:bg-[#AE232D]/5 rounded border border-slate-100 transition-all group/item">
                             <span className="text-[9px] font-mono truncate text-slate-600 group-hover/item:text-[#AE232D]">{row.phone_1}</span>
                             <button
                               onClick={() => handleCopy(row.phone_1, `${row.member_id}-phone`)}
                               className="hover:scale-110 transition-transform p-0.5"
                               title="Copy phone"
                             >
                               {copiedId === `${row.member_id}-phone` ? (
                                 <Check className="w-2.5 h-2.5 text-emerald-500" />
                               ) : (
                                 <Copy className="w-2.5 h-2.5 text-slate-300 group-hover/item:text-[#AE232D]/40 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                               )}
                             </button>
                           </div>
                         ) : <span className="text-slate-300 text-[10px] block text-center">—</span>}
                       </td>
                       <td className="p-1 px-2 font-mono text-[10px] text-slate-600">
                          {row.id_discord ? (
                            <div className="flex items-center gap-1.5">
                              <MessageSquare className="w-3 h-3 text-[#5865F2]" />
                              <span>{row.id_discord}</span>
                            </div>
                          ) : "—"}
                        </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DualScrollWrapper>

        <div className="px-6 py-5 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-[10px] font-normal text-[#004D57] uppercase tracking-widest">
            {totalRecords.toLocaleString()} Total Records &nbsp;·&nbsp; Showing page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || fetching}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-white border border-slate-200 rounded-xl font-normal text-[9px] uppercase tracking-widest text-[#004D57] hover:bg-[#004D57] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3 h-3" /> Prev
            </button>
            <span className="text-[10px] font-normal text-slate-400 tabular-nums">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || fetching}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-[#004D57] text-white rounded-xl font-normal text-[9px] uppercase tracking-widest hover:bg-[#005D69] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
